  // src/pages/services/ServiceList.jsx
  // v3 — 08 Jun 2026
  // Changes vs v2:
  // - Client field: dropdown → combobox (type to search + inline create)
  // - Client select → site suggestions auto-show
  // - Site select → client combobox auto-sync
  // - QuickCreateClient gets initialName pre-filled from typed text

  import { useEffect, useState } from "react";
  import { db } from "../../firebase";
  import { useNavigate, useLocation } from "react-router-dom";
  import {
    collection, getDocs, query, where
  } from "firebase/firestore";

  import {
    createServiceTask,
    getServiceTasks,
  } from "../../pages/services/serviceTaskService";

  import {
    SERVICE_STATUS,
    STATUS_UI,
  } from "../../constants/enums/serviceEnums";

  import {
    QuickCreateButton,
    QuickCreateModal,
    QuickCreateSite,
    QuickCreateClient,
  } from "../../components/quickcreate";
  import { fetchClientsWithSites } from "../../services/clientService";

  export default function ServiceList() {
    const navigate  = useNavigate();
    const user      = JSON.parse(localStorage.getItem("user") || "{}");
    const companyId = user.company_id || "";

    const [services,   setServices]   = useState([]);
    const [loading,    setLoading]    = useState(true);
    const [showCreate, setShowCreate] = useState(false);
    const [saving,     setSaving]     = useState(false);
    const [qcOpen,     setQcOpen]     = useState(null);
    const [filter,     setFilter]     = useState("all");
    const [siteMachines, setSiteMachines] = useState([]);

    // ── Master data ───────────────────────────────────────────────────────
    const [clients,        setClients]        = useState([]);
    const [sites,          setSites]          = useState([]);

    // ── Client combobox state ─────────────────────────────────────────────
    const [clientSearch,   setClientSearch]   = useState("");
    const [showClientSugg, setShowClientSugg] = useState(false);

    // ── Site suggestion state ─────────────────────────────────────────────
    const [siteSugg,       setSiteSugg]       = useState([]);

    // ── Form ──────────────────────────────────────────────────────────────
    const emptyForm = {
    client_id: "", client_name: "",
    site_id: "", site_name: "",
    complaint: "", address: "",
    location_link: "", scheduled_date: "", scheduled_time: "",
    job_category: "",
    machine_ids: [],
    project_id:   "",
    project_name: "",
  };
    const [form, setForm] = useState(emptyForm);

    // ── Load ──────────────────────────────────────────────────────────────
    // eslint-disable-line react-hooks/exhaustive-deps
    const location = useLocation();

    useEffect(() => {
      loadAll();
      fetchAllClients();
      fetchAllSites();

      // 🆕 Pre-fill from Project — if navigated from ProjectDetail
      if (location.state?.from_project) {
        const s = location.state;
        setClientSearch(s.client_name || "");
        setForm(f => ({
          ...f,
          client_id:    s.client_id    || "",
          client_name:  s.client_name  || "",
          site_id:      s.site_id      || "",
          site_name:    s.site_name    || "",
          job_category: "installation",
          project_id:   s.project_id   || "",
          project_name: s.project_name || "",
        }));
        setShowCreate(true);
      }

      }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Separate effect, keyed on location.state — so navigating here again
    // from Dashboard's "+ New Service" (even if ServiceList was already
    // mounted) still opens the modal. React Router doesn't always remount
    // the component on same-route navigation, so this can't live inside the
    // mount-only effect above.
    useEffect(() => {
      if (location.state?.open_create) {
        setShowCreate(true);
      }
    }, [location.state]);

    // Only clients that have at least one site (SOT check) are offered in the
    // combobox — prevents assigning a service job to a client with no site.
    const fetchAllClients = async () => {
      const list = await fetchClientsWithSites(companyId);
      setClients(
        list.sort((a, b) => (a.client_name || "").localeCompare(b.client_name || ""))
      );
    };

    const fetchAllSites = async () => {
      const snap = await getDocs(
        query(collection(db, "sites"), where("company_id", "==", companyId))
      );
      setSites(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    };

    const loadAll = async () => {
      try {
        setLoading(true);
        const data = await getServiceTasks({ company_id: companyId });

        // Technician scoping — consistent with the "My Projects" pattern.
        // Technicians only see jobs they're actually assigned to, not the
        // full company-wide list. Filtered client-side after the normal
        // company-scoped fetch — no separate Firestore query needed.
        const scoped = user.role === "technician"
          ? data.filter(s => {
              const myUid = user.uid || user.id;
              const assignedUids = s.assigned_techs?.map(t => t.id) || [];
              return assignedUids.includes(myUid) || s.assigned_to === myUid;
            })
          : data;

        setServices(scoped);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    // ── CLIENT COMBOBOX ───────────────────────────────────────────────────

    const handleClientSearch = (val) => {
      setClientSearch(val);
      // Clear selection when user types again
      setForm(f => ({ ...f, client_id: "", client_name: "", site_id: "", site_name: "" }));
      setSiteSugg([]);
      setShowClientSugg(val.trim().length > 0);
    };

    const selectClient = (c) => {
      setClientSearch(c.client_name);
      setForm(f => ({
        ...f,
        client_id:   c.id,
        client_name: c.client_name,
        site_id:     "",
        site_name:   "",
      }));
      setShowClientSugg(false);
      // Auto-show this client's sites
      setSiteSugg(sites.filter(s => s.client_id === c.id).slice(0, 10));
    };

    // ── SITE SEARCH ───────────────────────────────────────────────────────

    const handleSiteSearchAll = (val) => {
      setForm(f => ({ ...f, site_name: val, site_id: "" }));
      if (!val.trim()) {
        // Show client's sites when field is cleared
        if (form.client_id) {
          setSiteSugg(sites.filter(s => s.client_id === form.client_id).slice(0, 10));
        } else {
          setSiteSugg([]);
        }
        return;
      }
      const lower = val.toLowerCase();
      setSiteSugg(
        sites
          .filter(s =>
            s.site_name?.toLowerCase().includes(lower) &&
            (!form.client_id || s.client_id === form.client_id)
          )
          .slice(0, 10)
      );
    };

    const selectSiteWithClient = async (s) => {
      const linkedClient       = clients.find(c => c.id === s.client_id);
      const resolvedClientId   = linkedClient?.id          || s.client_id   || "";
      const resolvedClientName = linkedClient?.client_name || s.client_name || "";
      setClientSearch(resolvedClientName);
      setForm(f => ({
        ...f,
        site_id:       s.id,
        site_name:     s.site_name,
        client_id:     resolvedClientId,
        client_name:   resolvedClientName,
        address:       s.address       || f.address,
        location_link: s.location_link || f.location_link,
        machine_ids:   [],
      }));
      setSiteSugg([]);
      // Fetch machines for this site
      try {
        const snap = await getDocs(query(
          collection(db, "site_machines"),
          where("company_id", "==", companyId),
          where("site_id", "==", s.id)
        ));
        setSiteMachines(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (err) {
        console.error("fetch site machines error:", err);
        
      }
    };

    // ── CAPTURE LOCATION ──────────────────────────────────────────────────
    const captureLocation = () => {
      if (!navigator.geolocation) { alert("Location not supported"); return; }
      navigator.geolocation.getCurrentPosition(
        pos => {
          const link = `https://www.google.com/maps?q=${pos.coords.latitude},${pos.coords.longitude}`;
          setForm(f => ({ ...f, location_link: link }));
        },
        () => alert("Location permission denied")
      );
    };

    // ── CREATE SERVICE ────────────────────────────────────────────────────
    const handleCreate = async () => {
      try {
        setSaving(true);
        await createServiceTask({
          company_id:      companyId,
          client_id:       form.client_id,
          client_name:     form.client_name,
          site_id:         form.site_id,
          site_name:       form.site_name,
          complaint:       form.complaint,
          address:         form.address,
          location_link:   form.location_link,
          scheduled_date:  form.scheduled_date,
          scheduled_time:  form.scheduled_time,
          job_category:    form.job_category,
          machine_ids:     form.machine_ids,
          project_id:      form.project_id   || "",
          project_name:    form.project_name || "",
          created_by:      user.uid || user.id || "",
          created_by_name: user.name || "",
        });
        alert("Service Created ✅");
        setShowCreate(false);
        setForm(emptyForm);
        setClientSearch("");
        setSiteSugg([]);
        loadAll();
      } catch (err) {
        alert(err.message || "Error creating service");
      } finally {
        setSaving(false);
      }
    };

    // ── FILTER ────────────────────────────────────────────────────────────
    const filtered = filter === "all"
      ? services
      : services.filter(s => s.status === filter);

    const filterTabs = [
      { key: "all",                                     label: "All"               },
      { key: SERVICE_STATUS.OPEN,                       label: "Open"              },
      { key: SERVICE_STATUS.ASSIGNED,                   label: "Assigned"          },
      { key: SERVICE_STATUS.IN_PROGRESS,                label: "In Progress"       },
      { key: SERVICE_STATUS.PARTIAL,                    label: "Partial"           },
      { key: SERVICE_STATUS.COMPLETED_PENDING_APPROVAL, label: "Pending Approval"  },
      { key: SERVICE_STATUS.CLOSED,                     label: "Closed"            },
      { key: "completed",                               label: "Completed (Legacy)"},
    ];

    // ── Filtered client suggestions ───────────────────────────────────────
    const clientMatches = clients.filter(c =>
      c.client_name?.toLowerCase().includes(clientSearch.toLowerCase())
    );
    const exactMatch = clients.some(
      c => c.client_name?.toLowerCase() === clientSearch.toLowerCase()
    );

    // ── RENDER ────────────────────────────────────────────────────────────
    return (
      <div className="p-4 pb-20 bg-gray-50 min-h-screen">

        {/* HEADER */}
        <div className="flex items-center justify-between mb-5">
          <h1 className="text-xl font-bold text-slate-800">Service List</h1>
          {["owner", "sm", "manager"].includes(user.role) && (
            <button onClick={() => setShowCreate(true)}
              className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white px-4 py-2 rounded-xl text-sm font-semibold shadow-md">
              + New Service
            </button>
          )}
        </div>

        {/* FILTER TABS */}
        <div className="flex gap-2 overflow-x-auto pb-2 mb-4">
          {filterTabs.map(f => (
            <button key={f.key} onClick={() => setFilter(f.key)}
              className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold transition ${
                filter === f.key
                  ? "bg-blue-600 text-white"
                  : "bg-white text-slate-600 border border-slate-200"
              }`}>
              {f.label}
            </button>
          ))}
        </div>

        {/* LIST */}
        {loading ? (
          <p className="text-slate-400 text-sm text-center mt-10">Loading...</p>
        ) : filtered.length === 0 ? (
          <p className="text-slate-400 text-sm text-center mt-10">No services found</p>
        ) : (
          <div className="space-y-3">
            {filtered.map(s => {
              const ui = STATUS_UI[s.status] || { label: s.status || "Unknown", bg: "#f3f4f6", color: "#374151", border: "#d1d5db" };
              return (
                <div key={s.id} onClick={() => navigate(user.role === "technician" ? `/tasks/${s.id}` : `/services/${s.id}`)}
                  className="bg-white rounded-2xl shadow-sm border border-slate-100 p-4 cursor-pointer hover:shadow-md transition"
                  style={{ borderLeft: `4px solid ${ui.border}` }}>
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-semibold text-slate-800">
                        {s.client_name} → {s.site_name}
                      </p>
                      <p className="text-sm text-slate-500 mt-0.5">
    {s.job_category === "amc_visit"    ? "AMC Visit"          :
    s.job_category === "preventive"   ? "Preventive Maintenance" :
    s.job_category === "installation" ? "Installation"       :
    s.complaint || "No complaint"}
  </p>
                      {s.scheduled_date && (
                        <p className="text-xs text-slate-400 mt-1">
                          📅 {s.scheduled_date} {s.scheduled_time && `• ${s.scheduled_time}`}
                        </p>
                      )}
                      {s.job_category && (
    <span className={`inline-block mt-1 text-[10px] font-semibold px-2 py-0.5 rounded-full ${
      s.job_category === "complaint"    ? "bg-red-100 text-red-600"    :
      s.job_category === "amc_visit"    ? "bg-blue-100 text-blue-600"  :
      s.job_category === "preventive"   ? "bg-amber-100 text-amber-600":
      s.job_category === "installation" ? "bg-green-100 text-green-600":
      "bg-slate-100 text-slate-500"
    }`}>
      {s.job_category === "complaint"    ? "Complaint"    :
      s.job_category === "amc_visit"    ? "AMC Visit"   :
      s.job_category === "preventive"   ? "PM"          :
      s.job_category === "installation" ? "Installation" : s.job_category}
    </span>
  )}
                      {s.assigned_techs?.length > 0 && (
                        <p className="text-xs text-slate-400 mt-0.5">
                          👑 {s.assigned_techs[0]?.name}
                          {s.assigned_techs.length > 1 &&
                            ` • 👷 ${s.assigned_techs.slice(1).map(t => t.name).join(", ")}`}
                        </p>
                      )}
                    </div>
                    <span className="text-[11px] font-bold px-3 py-1 rounded-full shrink-0"
                      style={{ background: ui.bg, color: ui.color }}>
                      {ui.label}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* ════ CREATE MODAL ════ */}
        {showCreate && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl max-h-[90vh] overflow-y-auto">

              {/* Modal header */}
              <div className="flex items-center justify-between p-5 border-b">
                <h2 className="font-bold text-slate-800">
                  Create Service
                  {location.state?.from_project && (
                    <span className="ml-2 text-xs font-normal text-purple-600 bg-purple-50 px-2 py-0.5 rounded-full">
                      📁 {location.state.project_name}
                    </span>
                  )}
                </h2>
                <button onClick={() => {
                  setShowCreate(false);
                  setForm(emptyForm);
                  setClientSearch("");
                  setSiteSugg([]);
                }} className="text-slate-400 hover:text-slate-700 text-xl">✕</button>
              </div>

              <div className="p-5 space-y-3">

                {/* ── CLIENT COMBOBOX ── */}
                <div>
                  <label className="text-xs font-semibold text-slate-500 uppercase mb-1 block">
                    Client <span className="text-red-400">*</span>
                  </label>
                  <div className="flex gap-2 items-start">
                    <div className="relative flex-1">
                      <input
                        type="text"
                        placeholder="Type to search or create client..."
                        value={clientSearch}
                        onChange={e => handleClientSearch(e.target.value)}
                        onFocus={() => {
                          if (clientSearch.trim()) setShowClientSugg(true);
                        }}
                        onBlur={() => setTimeout(() => setShowClientSugg(false), 150)}
                        className="w-full border border-slate-200 p-3 rounded-xl text-sm outline-none focus:ring-2 focus:ring-cyan-400"
                      />
                      {/* Selected badge */}
                      {form.client_id && (
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] bg-cyan-100 text-cyan-700 px-2 py-0.5 rounded-full font-semibold">
                          ✓
                        </span>
                      )}

                      {/* Client suggestions dropdown */}
                      {showClientSugg && (
                        <div className="absolute left-0 top-full mt-1 w-full bg-white border border-slate-200 rounded-xl shadow-xl z-20 max-h-48 overflow-y-auto">

                          {/* Existing matches */}
                          {clientMatches.slice(0, 8).map(c => (
                            <div key={c.id} onMouseDown={() => selectClient(c)}
                              className="px-4 py-2.5 hover:bg-cyan-50 cursor-pointer text-sm border-b border-slate-100 last:border-0">
                              <p className="font-medium">{c.client_name}</p>
                              {c.contact && <p className="text-xs text-slate-400">{c.contact}</p>}
                            </div>
                          ))}

                          {/* Create new option */}
                          {clientSearch.trim() && !exactMatch && (
                            <div
                              onMouseDown={() => {
                                setShowClientSugg(false);
                                setQcOpen("client");
                              }}
                              className="px-4 py-2.5 hover:bg-indigo-50 cursor-pointer text-sm flex items-center gap-2 border-t border-slate-100">
                              <span className="w-5 h-5 rounded-md bg-indigo-100 flex items-center justify-center text-indigo-600 font-bold text-xs flex-shrink-0">+</span>
                              <span className="text-indigo-600 font-semibold">
                                Create "{clientSearch}"
                              </span>
                            </div>
                          )}

                          {/* No results */}
                          {clientMatches.length === 0 && exactMatch && (
                            <div className="px-4 py-3 text-sm text-slate-400 text-center">
                              No clients found
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    <QuickCreateButton
                      onClick={() => setQcOpen("client")}
                      label="Add new client"
                    />
                  </div>
                </div>

                {/* ── SITE SEARCH ── */}
                <div className="relative">
                  <label className="text-xs font-semibold text-slate-500 uppercase mb-1 block">
                    Site <span className="text-red-400">*</span>
                  </label>
                  <div className="flex gap-2 items-center">
                    <input
                      placeholder={form.client_id ? "Search site name..." : "Select client first"}
                      value={form.site_name}
                      onChange={e => handleSiteSearchAll(e.target.value)}
                      disabled={!form.client_id}
                      className="flex-1 border border-slate-200 p-3 rounded-xl text-sm outline-none focus:ring-2 focus:ring-cyan-400 disabled:bg-slate-50 disabled:text-slate-400"
                    />
                    <QuickCreateButton
                      onClick={() => setQcOpen("site")}
                      label="Add new site"
                      disabled={!form.client_id}
                    />
                  </div>

                  {/* Site suggestions */}
                  {siteSugg.length > 0 && (
                    <div className="absolute left-0 top-full mt-1 w-full bg-white border border-slate-200 rounded-xl shadow-xl z-10 max-h-48 overflow-y-auto">
                      {siteSugg.map(s => {
                        const linkedClient = clients.find(c => c.id === s.client_id);
                        const clientLabel  = linkedClient?.client_name || s.client_name || "";
                        return (
                          <div key={s.id} onClick={() => selectSiteWithClient(s)}
                            className="px-4 py-2.5 hover:bg-cyan-50 cursor-pointer text-sm border-b border-slate-100 last:border-0">
                            <p className="font-medium">{s.site_name}</p>
                            {clientLabel && (
                              <p className="text-xs text-cyan-600 font-medium mt-0.5">
                                👤 {clientLabel}
                              </p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Selected confirmation */}
                  {form.client_name && form.site_id && (
                    <p className="text-xs text-slate-400 mt-1">
                      Client: <span className="text-slate-600 font-medium">{form.client_name}</span>
                    </p>
                  )}
                </div>

                {/* ── COMPLAINT ── */}
                <div>
                  <label className="text-xs font-semibold text-slate-500 uppercase mb-1 block">
                    {form.job_category === "complaint" || !form.job_category
                      ? <> Complaint <span className="text-red-400">*</span></>
                      : "Work Description (optional)"
                    }
                  </label>
                  <textarea
                    placeholder="Describe the issue..."
                    value={form.complaint}
                    onChange={e => setForm(f => ({ ...f, complaint: e.target.value }))}
                    rows={3}
                    className="w-full border border-slate-200 p-3 rounded-xl text-sm outline-none focus:ring-2 focus:ring-cyan-400 resize-none"
                  />
                </div>

                {/* ── JOB CATEGORY ── */}
  <div>
    <label className="text-xs font-semibold text-slate-500 uppercase mb-1 block">
      Job Category
    </label>
    <div className="grid grid-cols-2 gap-2">
      {[
        { val: "complaint",   label: "Complaint / Repair",  color: "border-red-200 bg-red-50 text-red-700"     },
        { val: "amc_visit",   label: "AMC Visit",           color: "border-blue-200 bg-blue-50 text-blue-700"  },
        { val: "preventive",  label: "Preventive Maint.",   color: "border-amber-200 bg-amber-50 text-amber-700"},
        { val: "installation",label: "Installation",        color: "border-green-200 bg-green-50 text-green-700"},
      ].map(({ val, label, color }) => (
        <button
          key={val}
          type="button"
          onClick={() => setForm(f => ({ ...f, job_category: f.job_category === val ? "" : val }))}
          className={`py-2 px-3 rounded-xl text-xs font-semibold border transition ${
            form.job_category === val
              ? color
              : "border-slate-200 bg-white text-slate-500"
          }`}
        >
          {label}
        </button>
      ))}
    </div>
  </div>
  {/* ── MACHINES AT THIS SITE ── */}
  {siteMachines.length > 0 && (
    <div>
      <label className="text-xs font-semibold text-slate-500 uppercase mb-1 block">
        Machines at this Site
      </label>
      <div className="space-y-2 max-h-40 overflow-y-auto">
        {siteMachines.map(m => {
          const selected = form.machine_ids.includes(m.id);
          return (
            <div key={m.id}
              onClick={() => setForm(f => ({
                ...f,
                machine_ids: selected
                  ? f.machine_ids.filter(id => id !== m.id)
                  : [...f.machine_ids, m.id],
              }))}
              className={`flex items-center gap-3 p-2.5 rounded-xl border cursor-pointer transition ${
                selected
                  ? "bg-blue-50 border-blue-300"
                  : "bg-white border-slate-200 hover:bg-slate-50"
              }`}>
              <div className={`w-4 h-4 rounded border-2 flex-shrink-0 flex items-center justify-center ${
                selected ? "bg-blue-500 border-blue-500" : "border-slate-300"
              }`}>
                {selected && <span className="text-white text-[10px] font-bold">✓</span>}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-slate-700">{m.machine_name}</p>
                <p className="text-xs text-slate-400">{m.machine_type} • {m.brand} • S/N: {m.serial_no || "—"}</p>
              </div>
            </div>
          );
        })}
      </div>
      {form.machine_ids.length > 0 && (
        <p className="text-xs text-blue-600 mt-1 font-semibold">
          {form.machine_ids.length} machine(s) selected
        </p>
      )}
    </div>
  )}
                {/* ── DATE + TIME ── */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-semibold text-slate-500 uppercase mb-1 block">
                      Date <span className="text-red-400">*</span>
                    </label>
                    <input type="date" value={form.scheduled_date}
                      onChange={e => setForm(f => ({ ...f, scheduled_date: e.target.value }))}
                      className="w-full border border-slate-200 p-3 rounded-xl text-sm outline-none focus:ring-2 focus:ring-cyan-400" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-500 uppercase mb-1 block">Time</label>
                    <input type="time" value={form.scheduled_time}
                      onChange={e => setForm(f => ({ ...f, scheduled_time: e.target.value }))}
                      className="w-full border border-slate-200 p-3 rounded-xl text-sm outline-none focus:ring-2 focus:ring-cyan-400" />
                  </div>
                </div>

                {/* ── ADDRESS ── */}
                <div>
                  <label className="text-xs font-semibold text-slate-500 uppercase mb-1 block">Address</label>
                  <input
                    placeholder="Site address..."
                    value={form.address}
                    onChange={e => setForm(f => ({ ...f, address: e.target.value }))}
                    className="w-full border border-slate-200 p-3 rounded-xl text-sm outline-none focus:ring-2 focus:ring-cyan-400"
                  />
                </div>

                {/* ── LOCATION ── */}
                <div>
                  <label className="text-xs font-semibold text-slate-500 uppercase mb-1 block">
                    Location Link
                  </label>
                  <input
                    placeholder="Paste Google Maps link..."
                    value={form.location_link}
                    onChange={e => setForm(f => ({ ...f, location_link: e.target.value }))}
                    className="w-full border border-slate-200 p-3 rounded-xl text-sm outline-none focus:ring-2 focus:ring-cyan-400"
                  />
                  <button onClick={captureLocation}
                    className="mt-2 text-xs bg-green-600 text-white px-3 py-1.5 rounded-lg">
                    📍 Capture My Location
                  </button>
                  {form.location_link && (
                    <p className="text-xs text-green-600 mt-1">✔ Location added</p>
                  )}
                </div>

                {/* ── SAVE ── */}
                <button onClick={handleCreate} disabled={saving}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-xl font-semibold text-sm transition disabled:opacity-50">
                  {saving ? "Creating..." : "Create Service"}
                </button>

              </div>
            </div>
          </div>
        )}

        {/* ════ QUICK CREATE MODALS ════ */}

        {/* Client */}
        <QuickCreateModal
          open={qcOpen === "client"}
          onClose={() => setQcOpen(null)}
          title="Quick Create — Client"
        >
          <QuickCreateClient
            companyId={companyId}
            initialName={clientSearch}
            onCreated={(newClient) => {
              setClients(prev =>
                [...prev, newClient].sort((a, b) =>
                  (a.client_name || "").localeCompare(b.client_name || "")
                )
              );
              setClientSearch(newClient.client_name);
              setForm(f => ({
                ...f,
                client_id:   newClient.id,
                client_name: newClient.client_name,
                site_id:     "",
                site_name:   "",
              }));
              setSiteSugg([]);
              setQcOpen(null);
            }}
            onCancel={() => setQcOpen(null)}
          />
        </QuickCreateModal>

        {/* Site */}
        <QuickCreateModal
          open={qcOpen === "site"}
          onClose={() => setQcOpen(null)}
          title="Quick Create — Site"
        >
          <QuickCreateSite
            companyId={companyId}
            clientId={form.client_id}
            clientName={form.client_name}
            onCreated={(newSite) => {
              setSites(prev => [...prev, newSite]);
              setForm(f => ({
                ...f,
                site_id:   newSite.id,
                site_name: newSite.site_name,
              }));
              setQcOpen(null);
            }}
            onCancel={() => setQcOpen(null)}
          />
        </QuickCreateModal>

      </div>
    );
  }
